import { fetchPresignedUrls } from './api';

const cache = new Map(); // key → { img: HTMLImageElement, promise: Promise }

// Batch queue: collect keys requested in the same tick, fetch URLs in one call
let pendingKeys = [];
let flushTimer = null;
let batchResolvers = new Map(); // key → { resolve, reject }

const flushBatch = () => {
  const keys = [...new Set(pendingKeys)];
  const resolvers = new Map(batchResolvers);
  pendingKeys = [];
  batchResolvers = new Map();
  flushTimer = null;

  if (keys.length === 0) return;

  fetchPresignedUrls(keys)
    .then(urls => {
      keys.forEach(key => {
        const url = urls[key];
        const resolver = resolvers.get(key);
        if (url && resolver) {
          resolver.resolve(url);
        } else if (resolver) {
          resolver.reject(new Error(`No presigned URL returned for ${key}`));
        }
      });
    })
    .catch(err => {
      keys.forEach(key => {
        const resolver = resolvers.get(key);
        if (resolver) resolver.reject(err);
      });
    });
};

const getPresignedUrl = (s3Key) => {
  // If already queued in this batch, return the existing promise
  if (batchResolvers.has(s3Key)) {
    return batchResolvers.get(s3Key).urlPromise;
  }

  let resolve, reject;
  const urlPromise = new Promise((res, rej) => { resolve = res; reject = rej; });

  batchResolvers.set(s3Key, { resolve, reject, urlPromise });
  pendingKeys.push(s3Key);

  // Flush after a short delay to collect all keys from the current render cycle
  if (!flushTimer) {
    flushTimer = setTimeout(flushBatch, 10);
  }

  return urlPromise;
};

export const loadImage = async (s3Key) => {
  if (!s3Key) return null;
  if (cache.has(s3Key)) return cache.get(s3Key).promise;

  const entry = {};
  entry.promise = getPresignedUrl(s3Key).then(url => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { entry.img = img; resolve(img); };
      img.onerror = reject;
      img.src = url;
    });
  }).catch(err => {
    // Remove failed entries so they can be retried
    cache.delete(s3Key);
    throw err;
  });
  cache.set(s3Key, entry);
  return entry.promise;
};

export const getCachedImage = (s3Key) => cache.get(s3Key)?.img || null;

export const clearCache = () => cache.clear();
